import styled from '@emotion/styled';
import {
  Button,
  Drawer,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  notification,
} from 'antd';
import moment from 'moment';
import { ReactNode, useEffect, useState } from 'react';
import { FaLock, FaLockOpen, FaPen, FaShieldAlt, FaUser, FaUserCheck, FaUsers } from 'react-icons/fa';
import { FiRefreshCcw, FiSearch } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { AgenceEntity } from '../../../../entities/agence.entity';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { ProfilEntity } from '../../../../entities/profil.entity';
import { UserEntity } from '../../../../entities/utilisateur.entity';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { Layout } from '../../../shared/Layout';
import { KYCUserForm } from '../../emissionTitre/components/KYCUserForm';
import { ProfilForm } from '../components/ProfilForm';
import { UserForm } from '../components/UserForm';
import {
  deverouillerCompte,
  getUtilisateurDetails,
  verouillerCompte,
} from '../network';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  display: flex;
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 92% 8%, rgba(255, 176, 0, 0.08), transparent 22%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  .bread-crum span {
    color: var(--mt-text);
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-text);
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: var(--mt-row-hover) !important;
      cursor: pointer;
      color: var(--mt-text);
    }
  }

  .content {
    padding: 12px;
    width: 100%;
    background: transparent;
    color: var(--mt-text);
  }

  .admin-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .admin-hero h1 {
    margin: 6px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
  }

  .admin-hero p {
    max-width: 760px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
  }

  .hero-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ghost-action {
    border-radius: 999px;
    border-color: rgba(255, 176, 0, 0.28);
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
    font-family: Poppins-B;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .page-toolbar,
  .table-shell {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 78px;
    padding: 13px;
  }

  .kpi-card .label {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .kpi-card .value {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 22px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(255, 176, 0, 0.24);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .page-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 360px) 1fr auto;
    align-items: center;
    gap: 12px;
    margin: 12px 0;
    padding: 12px;
  }

  .search-box {
    position: relative;
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 11px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 999px;
    padding-left: 34px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .table-shell {
    overflow: hidden;
    padding: 12px;
  }

  .action-button {
    display: inline-grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  @media (max-width: 900px) {
    .content {
      padding: 18px 12px;
    }

    .admin-hero,
    .page-toolbar {
      grid-template-columns: 1fr;
      flex-direction: column;
      align-items: flex-start;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export const GestionUserProfilPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [agence, setAgence] = useState<AgenceEntity>();
  const [showProfilDrawer, setShowProfilDrawer] = useState(false);
  const [showUpdateUserDrawer, setShowUpdateUserDrawer] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [filterUsers, setFilterUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserEntity>();
  const [selectedUserPro, setSelectedUserPro] = useState<ProfilEntity[]>([]);
  const [textDisplay, setTextDisplay] = useState('');
  const [toActivate, setToActivate] = useState(false);
  const [showDrawer1, setShowDrawer1] = useState(false);

  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  const fetchData = () => {
    setIsLoading(true);
    getUtilisateurDetails(connectedUser.id).then(({ data }) => {
      setUsers(data);
      setFilterUsers(data);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeAccountStatus = () => {
    if (selectedUser) {
      if (toActivate) {
        deverouillerCompte(selectedUser.url).then(({ data }) => {
          notification.success({
            message: 'Succès',
            description: 'Le compte a été dévéroullé avec succès',
          });
          setUsers(
            users.map((u) => {
              if (u.id === data.id) {
                u.actif = true;
                return u;
              } else {
                return u;
              }
            }),
          );
          setFilterUsers(
            filterUsers.map((u) => {
              if (u.id === data.id) {
                u.actif = true;
                return u;
              } else {
                return u;
              }
            }),
          );
          setIsLoading(false);
          setShowModal(false);
        });
      } else {
        verouillerCompte(selectedUser.url).then(({ data }) => {
          notification.success({
            message: 'Succès',
            description: 'Le compte a été véroullé avec succès',
          });
          setUsers(
            users.map((u) => {
              if (u.id === data.id) {
                u.actif = false;
                return u;
              } else {
                return u;
              }
            }),
          );
          setFilterUsers(
            filterUsers.map((u) => {
              if (u.id === data.id) {
                u.actif = false;
                return u;
              } else {
                return u;
              }
            }),
          );
          setIsLoading(false);
          setShowModal(false);
        });
      }
    }
  };

  const setColor = (code: string) => {
    switch (code) {
      case 'admin':
        return '#f50';
      case 'resp_commercial':
        return '#3c096c';
      case 'commercial':
        return '#f9a825';
      case 'responsable':
        return '#108ee9';
      case 'chef_agence':
        return '#8d6e63';
      case 'gestionnaire':
        return '#5f0f40';
      case 'sadmin':
        return '#fb8b24';
    }
  };

  const COLUMNS = [
    {
      title: 'Nom',
      dataIndex: 'nom',
      flex: 1,
    },
    {
      title: 'Prénom',
      dataIndex: 'prenom',
      flex: 1,
    },
    // {
    //   title: 'Email',
    //   dataIndex: 'email',
    //   flex: 1,
    // },
    {
      title: 'Adresse',
      dataIndex: 'adresse',
      flex: 1,
    },
    {
      title: 'Contact',
      dataIndex: 'mobile',
      flex: 1,
    },
    {
      title: 'Agence',
      render: (cell: any, row: any) => row.agence?.nom,
      flex: 1,
    },
    {
      title: 'Partenaire',
      render: (cell: any, row: any) => row.partner?.nom,
      flex: 1,
    },
    {
      title: 'Date création',
      dataIndex: 'created_at',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
      flex: 1,
    },
    {
      title: 'Profils',
      render: (cell: any, row: any) => {
        return (
          <Space>
            {row.profils.length === 0 ? (
              <Tag>--</Tag>
            ) : (
              row.profils.map((pro: any) => (
                <Tag key={pro.id} color={setColor(pro.code_profil)}>
                  {pro.nom}
                </Tag>
              ))
            )}
          </Space>
        );
      },
      flex: 2,
    },
    {
      title: 'Statut',
      render: (cell: any, row: any) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {row.actif ? (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'green',
              }}
            ></div>
          ) : (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'red',
              }}
            ></div>
          )}
        </div>
      ),
      flex: 1,
    },
    {
      title: 'Action',
      render: (cell: any, row: any) => (
        <Space>
          <Button
            className='action-button'
            icon={<FaPen />}
            onClick={() => {
              setSelectedUser(row);
              setShowUpdateUserDrawer(true);
              setAgence(row.agence);
            }}
          />
          <Button
            className='action-button'
            icon={<FaUser />}
            onClick={() => {
              setSelectedUserPro(row.profils);
              setSelectedUser(row);
              setShowProfilDrawer(true);
            }}
          />
          <Button
            className='action-button'
            icon={<FaUserCheck />}
            onClick={() => {
              setSelectedUser(row);
              setShowDrawer1(true);
            }}
          />
          {row.actif ? (
            <Button
              className='action-button'
              icon={<FaLockOpen />}
              onClick={() => {
                setSelectedUser(row);
                setTextDisplay(
                  'Voulez-vous désactiver le compte de cet utilisateur ?',
                );
                setToActivate(false);
                setShowModal(true);
              }}
            />
          ) : (
            <Button
              className='action-button'
              icon={<FaLock />}
              onClick={() => {
                setSelectedUser(row);
                setTextDisplay(
                  'Voulez-vous réactiver le compte de cet utilisateur ?',
                );
                setToActivate(true);
                setShowModal(true);
              }}
            />
          )}
        </Space>
      ),
      flex: 1,
    },
  ];

  return (
    <Layout>
      <Container>
        <div className='content'>
          <div style={{ marginTop: 0 }}>
            <section className='admin-hero'>
              <div>
                <span className='eyebrow'>Administration</span>
                <h1>Utilisateurs et profils</h1>
                <p>
                  Contrôlez les accès, les rôles, les statuts de compte et les
                  validations KYC des opérateurs.
                </p>
              </div>
              <div className='hero-actions'>
                <Button className='ghost-action' icon={<FiRefreshCcw />} onClick={fetchData}>
                  Rafraîchir
                </Button>
              </div>
            </section>

            <section className='kpi-grid'>
              <KpiCard label='Utilisateurs' value={filterUsers.length || '--'} icon={<FaUsers />} />
              <KpiCard label='Comptes actifs' value={filterUsers.filter((u) => u.actif).length || '--'} icon={<FaUserCheck />} />
              <KpiCard label='Profils assignés' value={filterUsers.reduce((total, user) => total + (user.profils?.length || 0), 0) || '--'} icon={<FaShieldAlt />} />
            </section>

            <div className='page-toolbar'>
              <div className='search-box'>
                <FiSearch />
              <Input
                placeholder='Rechercher...'
                onChange={(e) => {
                  setFilterUsers(
                    users.filter(
                      (u) =>
                        u.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.prenom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.agence?.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.partner?.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.adresse
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()),
                    ),
                  );
                }}
              />
              </div>
              <span />
              <PulseLoader
                size={5}
                margin={2}
                color={YELLOW}
                loading={isLoading}
              />
            </div>

            <div className='table-shell'>
            <Table
              columns={COLUMNS}
              dataSource={filterUsers}
              rowClassName={(row, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              size='small'
              style={{ zoom: 0.9 }}
              scroll={{ x: 1180 }}
              pagination={false}
              loading={isLoading}
            />
            </div>
          </div>
        </div>
        <Drawer
          title='Gestion des profils'
          placement='right'
          width='min(100vw, 520px)'
          onClose={() => setShowProfilDrawer(false)}
          open={showProfilDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <ProfilForm user={selectedUser} userProfils={selectedUserPro} />
        </Drawer>
        <Drawer
          title="Mettre à jour l'utilisateur"
          placement='right'
          width='min(100vw, 560px)'
          onClose={() => setShowUpdateUserDrawer(false)}
          open={showUpdateUserDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <UserForm
            userToUpdate={selectedUser}
            agence={agence as AgenceEntity}
            setShowDrawer={setShowUpdateUserDrawer}
            getNewUser={(user) => {}}
          />
        </Drawer>
        <Modal
          className='dark-modal'
          open={showModal}
          title='Confirmation'
          onCancel={() => setShowModal(false)}
          footer={[
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={() => changeAccountStatus()}
                style={{ borderRadius: 10 }}
              >
                Oui
              </Button>
              <Button
                className='butt'
                style={{ borderRadius: 10 }}
                onClick={() => setShowModal(false)}
              >
                Non
              </Button>
            </div>,
          ]}
        >
          <p>{textDisplay} </p>
        </Modal>
        <Drawer
          title='Verifier le KYC'
          placement='right'
          width='min(100vw, 620px)'
          onClose={() => setShowDrawer1(false)}
          open={showDrawer1}
          closeIcon={<MdClose size={20} />}
        >
          <KYCUserForm
            userToUpdate={selectedUser}
            setShowDrawer={setShowDrawer1}
            getNewClient={(cli) => {
              setUsers([...users, cli]);
              setFilterUsers([...users, cli]);
            }}
          />
        </Drawer>
      </Container>
    </Layout>
  );
};

const KpiCard = ({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) => (
  <div className='kpi-card'>
    <div>
      <span className='label'>{label}</span>
      <strong className='value'>{value}</strong>
    </div>
    <span className='icon'>{icon}</span>
  </div>
);
